import React, { useMemo, useState } from "react";
import { FaUpload, FaFilter, FaTimes } from "../../utils/icons";
import { Button } from "@/design-system/components/react";
import { UploadRow } from "./UploadRow";
import type { UploadListItem, UploadStatus } from "./types";
import type { BatchCase, BatchPhase } from "../../hooks/useBatchUpload";
import { BatchUploadSidebarRow } from "./BatchUploadSidebarRow";

/**
 * Left panel of the Upload page (LU-A10) — worklist-shaped.
 *
 * `Upload study` primary button at the top (Andrea: top of left panel), then the
 * "Your uploads" list rendered with worklist-identical rows. Header carries a
 * worklist-style status **Filter** affordance (Ready / Processing / Failed),
 * and rows expose a delete on hover. (The Export CSV affordance lives in the
 * right-pane header band — see `UploadPageView`.)
 */
interface UploadSidebarProps {
  uploads: UploadListItem[];
  selectedId?: string | null;
  /** Selecting a row shows its detail in the right pane. */
  onSelect: (id: string) => void;
  /** "Upload study" → right pane returns to the dropzone (new upload). */
  onNewUpload: () => void;
  /** Remove an upload from the list (e.g. dismiss a failed one). */
  onDelete?: (id: string) => void;
  /** True while a brand-new upload is being composed (no row selected yet). */
  newUploadActive?: boolean;
  batch?: { phase: BatchPhase; cases: BatchCase[] };
  batchSelected?: boolean;
  onSelectBatch?: () => void;
}

const STATUS_ORDER: { key: UploadStatus; label: string }[] = [
  { key: "ready", label: "Ready" },
  { key: "processing", label: "Processing" },
  { key: "failed", label: "Failed" },
];

export const UploadSidebar: React.FC<UploadSidebarProps> = ({
  uploads,
  selectedId,
  onSelect,
  onNewUpload,
  onDelete,
  newUploadActive = false,
  batch,
  batchSelected = false,
  onSelectBatch,
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  // Default: all statuses visible. A status is filtered out when removed here.
  const [active, setActive] = useState<Set<UploadStatus>>(
    () => new Set<UploadStatus>(["ready", "processing", "failed"]),
  );
  const isFiltered = active.size < STATUS_ORDER.length;

  const counts = useMemo(() => {
    const c: Record<UploadStatus, number> = { ready: 0, processing: 0, failed: 0 };
    uploads.forEach((u) => (c[u.status] += 1));
    return c;
  }, [uploads]);

  const visible = useMemo(() => uploads.filter((u) => active.has(u.status)), [uploads, active]);

  const toggleStatus = (s: UploadStatus) =>
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      // Never allow an empty filter (would hide everything with no recovery).
      return next.size === 0 ? new Set<UploadStatus>([s]) : next;
    });

  const clearFilter = () => setActive(new Set<UploadStatus>(["ready", "processing", "failed"]));

  return (
    // No border-r on the aside: the top band (Upload study + New upload header)
    // reads as one continuous bar. The left/right divider is on the sections
    // BELOW the band instead, so it doesn't cut through the header row.
    <aside className="flex flex-col min-h-0">
      <div className="flex h-[60px] shrink-0 items-center border-b border-border bg-bg-2 px-3">
        <Button
          variant="default"
          className={`w-full ${newUploadActive ? "ring-1 ring-signal-ring" : ""}`}
          onClick={onNewUpload}
          data-testid="upload-new-button"
        >
          <FaUpload className="h-4 w-4" />
          Upload study
        </Button>
      </div>

      <div className="relative flex items-center justify-between gap-2 border-b border-r border-border px-3.5 py-2.5 shrink-0">
        <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-fg-dim">
          Your uploads
        </span>
        <div className="flex items-center gap-1.5">
          {isFiltered && (
            <button
              onClick={clearFilter}
              className="flex items-center gap-1 rounded-xs border border-signal-stroke px-2 py-1 font-mono text-[9px] text-signal-text-tint transition-colors hover:bg-signal-tint"
              title="Clear filter"
            >
              {active.size} of {STATUS_ORDER.length}
              <FaTimes className="h-2 w-2" />
            </button>
          )}
          <button
            onClick={() => setFilterOpen((o) => !o)}
            data-testid="upload-filter-button"
            className={`flex items-center gap-1 rounded-xs border px-2 py-1 font-mono text-[9px] transition-colors ${
              filterOpen || isFiltered
                ? "border-signal-stroke text-signal-text-tint"
                : "border-border-strong text-fg-muted hover:text-fg"
            }`}
            title="Filter uploads"
          >
            <FaFilter className="h-2.5 w-2.5" />
            Filter
          </button>

          {filterOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setFilterOpen(false)} />
              <div className="absolute right-0 top-full z-30 mt-1 w-52 rounded-xs border border-border-strong bg-bg-2 p-2 shadow-xl shadow-black/40">
                <div className="px-1.5 pb-1.5 font-mono text-[9px] uppercase tracking-[0.14em] text-fg-dim">
                  Status
                </div>
                {STATUS_ORDER.map((s) => {
                  const on = active.has(s.key);
                  return (
                    <button
                      key={s.key}
                      onClick={() => toggleStatus(s.key)}
                      className="flex w-full items-center gap-2.5 rounded-xs px-1.5 py-1.5 text-left text-xs text-fg-muted transition-colors hover:bg-surface-2"
                    >
                      <span
                        className={`grid h-3.5 w-3.5 flex-none place-items-center rounded-[2px] border ${
                          on ? "border-signal bg-signal" : "border-border-strong"
                        }`}
                      >
                        {on && (
                          <span className="h-1.5 w-1 rotate-45 border-b-[1.5px] border-r-[1.5px] border-fg" />
                        )}
                      </span>
                      <span className={on ? "text-fg" : ""}>{s.label}</span>
                      <span className="ml-auto font-mono text-[10px] text-fg-faint">
                        {counts[s.key]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto border-r border-border custom-scrollbar"
        data-testid="upload-list"
      >
        {batch && onSelectBatch && (
          <BatchUploadSidebarRow
            phase={batch.phase}
            cases={batch.cases}
            active={batchSelected}
            onSelect={onSelectBatch}
          />
        )}
        {uploads.length === 0 ? (
          !batch ? (
            <div className="px-4 py-8 text-center text-xs text-fg-dim">
              Nothing uploaded yet. Drop a CT study to start.
            </div>
          ) : null
        ) : visible.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-fg-dim">
            No uploads match this filter.
          </div>
        ) : (
          visible.map((u) => (
            <UploadRow
              key={u.id}
              item={u}
              active={u.id === selectedId}
              onSelect={onSelect}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </aside>
  );
};
